import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Stack, Tab, Table,
  TableBody, TableCell, TableRow, Tabs, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { Operator, SiteSummary } from '../../api/types';
import { useMyPermissions, useUsersList } from '../../api/queries';
import { CashDeskOptionsPanel } from '../cashdesk/CashDeskOptionsPage';
import { FloorLayoutEditor } from '../cashdesk/FloorLayoutPage';
import { CashDeskTerminalsPanel } from '../cashdesk/CashDeskTerminalsPage';
import { CashDeskActivitiesPanel } from '../cashdesk/CashDeskActivitiesPage';

/**
 * The tabbed site view (owner request 2026-08-23) — everything that belongs
 * to ONE site in one place, mirroring the machine view's tab pattern:
 * details, cash-desk configuration, the floor-layout editor, the terminal
 * registry, the shift ledger, and the staff registered to the site. The
 * Sites grid's row menu deep-links straight to a tab.
 */

export type SiteTab = 'details' | 'cashdesk' | 'layout' | 'terminals' | 'shifts' | 'staff';

export function SiteDetailDialog({ site, initialTab, operatorById, onClose }: {
  site: SiteSummary | null;
  initialTab: SiteTab;
  operatorById: Map<string, Operator>;
  onClose: () => void;
}) {
  const open = site !== null;
  const [tab, setTab] = useState<SiteTab>(initialTab);
  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  const perms = useMyPermissions();
  const has = (code: string) => perms.data?.codes?.includes(code) ?? false;

  const tabs = useMemo(() => [
    { key: 'details' as const,   label: 'Details',       show: true },
    { key: 'cashdesk' as const,  label: 'Cash desk',     show: has('cashdesk.options.manage') },
    { key: 'layout' as const,    label: 'Floor layout',  show: has('cashdesk.layout.manage') },
    { key: 'terminals' as const, label: 'Terminals',     show: has('cashdesk.terminals.manage') },
    { key: 'shifts' as const,    label: 'Shifts',        show: has('cashdesk.activity.read') },
    { key: 'staff' as const,     label: 'Staff',         show: has('users.view') },
  ].filter((t) => t.show), [perms.data?.codes]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!site) return null;
  const active = tabs.some((t) => t.key === tab) ? tab : 'details';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { minHeight: '80vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>{site.displayName}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {site.code}
          </Typography>
          <Chip size="small" label={site.status}
            color={site.status === 'active' ? 'success' : 'default'} />
        </Stack>
        <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>
      <Tabs value={active} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable" allowScrollButtonsMobile>
        {tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} data-testid={`site-tab-${t.key}`} />)}
      </Tabs>
      <DialogContent>
        {active === 'details'   && <DetailsTab site={site} operatorById={operatorById} />}
        {active === 'cashdesk'  && <CashDeskOptionsPanel siteId={site.id} />}
        {active === 'layout'    && <FloorLayoutEditor siteId={site.id} />}
        {active === 'terminals' && <CashDeskTerminalsPanel siteId={site.id} />}
        {active === 'shifts'    && <CashDeskActivitiesPanel siteId={site.id} siteLabel={site.displayName} />}
        {active === 'staff'     && <StaffTab site={site} />}
      </DialogContent>
    </Dialog>
  );
}

function DetailsTab({ site, operatorById }: {
  site: SiteSummary; operatorById: Map<string, Operator>;
}) {
  const op = operatorById.get(site.operatorId);
  const rows: [string, ReactNode][] = [
    ['Site', site.displayName],
    ['Code', <code key="c">{site.code}</code>],
    ['Operator', op ? `${op.displayName} (${op.code})` : site.operatorId],
    ['Address', site.addressLine1],
    ['City', site.city],
    ['State / Prov.', fmtOrEmDash(site.stateProvince)],
    ['Postal code', fmtOrEmDash(site.postalCode)],
    ['Time zone', site.timeZone],
    ['Release channel', site.releaseChannel],
    ['Gaming day offset', `${site.gamingDayOffsetMinutes} min after local midnight`],
    ['Status', site.status],
    // Audit-trail block (house rule: every detail view carries it).
    ['Created', fmtDateTime(site.createdAt)],
    ['Updated', fmtDateTime(site.updatedAt)],
  ];
  return (
    <Table size="small" sx={{ maxWidth: 640 }}>
      <TableBody>
        {rows.map(([label, value]) => (
          <TableRow key={label}>
            <TableCell sx={{ color: 'text.secondary', width: 180, border: 0 }}>{label}</TableCell>
            <TableCell sx={{ border: 0 }}>{value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Users REGISTERED to this site (auth.user_site) — the pool the cash-desk
 *  roster and site work draw from. */
function StaffTab({ site }: { site: SiteSummary }) {
  const users = useUsersList(site.operatorId, site.id);
  const q = useQuery({ queryKey: users.queryKey, queryFn: users.listFn });

  if (q.isLoading) return <Typography color="text.secondary">Loading staff…</Typography>;
  const rows = q.data ?? [];
  if (rows.length === 0) {
    return (
      <Typography color="text.secondary" data-testid="staff-empty">
        No users are registered to this site yet — assign sites to users on the Users page.
      </Typography>
    );
  }
  return (
    <Box sx={{ maxWidth: 720 }}>
      <Table size="small">
        <TableBody>
          {rows.map((u) => (
            <TableRow key={u.id} data-testid={`staff-${u.id}`}>
              <TableCell sx={{ width: 260 }}>
                <Stack sx={{ py: 0.25 }}>
                  <Typography variant="body2">{u.displayName}</Typography>
                  <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Chip size="small" label={u.status}
                  color={u.status === 'Active' ? 'success' : 'default'} />
              </TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                {u.roleCount} role{u.roleCount === 1 ? '' : 's'} · {u.siteCount} site{u.siteCount === 1 ? '' : 's'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
